'use client'

import { useCallback, useMemo } from 'react'
import createApp from '@shopify/app-bridge'
import { Redirect } from '@shopify/app-bridge/actions'
import { useBoot } from '@/app/(app)/context/BootContext'

const APP_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://app.sigmasense.pk'
const API_KEY = (process.env.NEXT_PUBLIC_SHOPIFY_API_KEY || '').trim()

/**
 * Extract store handle for billing redirects.
 * Tries: 1) admin pathname, 2) host param (base64), 3) shop param.
 */
export function getStoreId(): string | null {
  if (typeof window === 'undefined') return null

  // 1) Preferred: admin path
  const pathMatch = window.location.pathname.match(/\/store\/([^/]+)\//)
  if (pathMatch?.[1]) return pathMatch[1]

  // 2) Shopify host param (base64 encoded)
  const params = new URLSearchParams(window.location.search)
  const host = params.get('host')
  if (host) {
    try {
      const decoded = atob(host.replace(/-/g, '+').replace(/_/g, '/'))
      const hostMatch = decoded.match(/\/store\/([^/]+)\//)
      if (hostMatch?.[1]) return hostMatch[1]
    } catch {
      // ignore decode errors
    }
  }

  // 3) Fallback to shop param
  const shop = params.get('shop')
  if (shop) return shop.split('.')[0]

  return null
}

/** Modern App Bridge config: object-based API (apiKey, host, forceRedirect). No legacy positional args. */
interface AppBridgeConfig {
  apiKey: string
  host: string
  forceRedirect: boolean
}

function getHostFromWindow(): string | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  return params.get('host') || hashParams.get('host') || window.localStorage.getItem('shopify-host')
}

/**
 * Redirect to URL. In embedded mode (host present), uses Shopify App Bridge
 * Redirect.Action.REMOTE only — never window.location, window.top, or router.
 * Falls back to window.location.href only in direct mode (no host = not embedded).
 * Call only from useEffect/callbacks; pass host from useBoot when available.
 */
export function redirectToUrl(url: string, options?: { host?: string; shop?: string }) {
  const host = options?.host ?? getHostFromWindow()
  const isEmbedded = !!host

  if (isEmbedded && host && API_KEY) {
    try {
      const config: AppBridgeConfig = {
        apiKey: API_KEY,
        host,
        forceRedirect: true,
      }
      const app = createApp(config)
      const redirect = Redirect.create(app)
      redirect.dispatch(Redirect.Action.REMOTE, url)
    } catch {
      // In embedded mode, do not fallback to window.location (causes iframe errors)
    }
    return
  }

  // Direct mode only: safe to use window.location
  window.location.href = url
}

/**
 * Hook that provides App Bridge–based redirect for auth URLs.
 * Use this instead of window.location or window.top.location in embedded mode.
 * Uses BootContext for host/shop (no window access during render).
 */
export function useAppBridgeRedirect() {
  const { bootHost, bootShop } = useBoot()
  const effectiveHost = bootHost
  const effectiveShop = bootShop
  const isEmbedded = !!(effectiveHost && effectiveShop)

  const redirectToAuth = useCallback(() => {
    if (!effectiveShop) return
    if (!effectiveHost) {
      window.location.href = `${APP_URL.replace(/\/$/, '')}/signin`
      return
    }
    const authUrl = `${APP_URL}/api/auth?shop=${encodeURIComponent(effectiveShop)}&host=${encodeURIComponent(effectiveHost)}`
    redirectToUrl(authUrl, { host: effectiveHost, shop: effectiveShop })
  }, [effectiveHost, effectiveShop])

  const redirectTo = useCallback(
    (url: string) => {
      if (isEmbedded) {
        redirectToUrl(url, { host: effectiveHost ?? undefined, shop: effectiveShop ?? undefined })
      } else {
        window.location.href = url
      }
    },
    [isEmbedded, effectiveHost, effectiveShop]
  )

  return useMemo(
    () => ({
      redirectToAuth,
      redirectTo,
      isEmbedded,
      effectiveHost,
      effectiveShop,
    }),
    [redirectToAuth, redirectTo, isEmbedded, effectiveHost, effectiveShop]
  )
}
